import { useEffect, useState } from 'react';
import type { CatalogProvider, Category, ModelSummary, ProductModel, ViewId } from '@ox/schema';
import { useEditorStore } from '../../store/editor-store';
import { SizeGuideModal } from '../SizeGuideModal';
import { BottomSheet } from './BottomSheet';
import { swatchStyle } from '../../lib/swatchStyle';
import {
  modelShowsColors,
  modelShowsSizes,
  modelSizeAxisLabel,
  pluralizeAxisLabel,
} from '../../lib/modelAxes';

export function ProductDetailsSheet({ onClose }: { onClose: () => void }) {
  const model = useEditorStore((s) => s.model);
  const colorKey = useEditorStore((s) => s.document.selection.colorKey);
  const setColor = useEditorStore((s) => s.setColor);
  const color = model?.colors.find((c) => c.key === colorKey) ?? model?.colors[0];
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const sizeGuideUrl = model?.sizeGuideUrl?.trim();
  const hasSizeGuide = !!sizeGuideUrl && sizeGuideUrl !== '#';
  const showColors = modelShowsColors(model);
  const showSizes = modelShowsSizes(model);
  const sizeAxis = pluralizeAxisLabel(modelSizeAxisLabel(model));

  if (!model) return null;

  return (
    <>
      <BottomSheet title="Detalles del producto" onClose={onClose} tall>
        <img className="ox-m-details-hero" src={color?.mockups.front?.url} alt="" />
        <h2 className="ox-m-details-name">{model.name}</h2>
        {model.rating && (
          <p className="ox-m-rating">
            ★ {model.rating.value} ({model.rating.count} reviews)
          </p>
        )}
        <p className="ox-m-details-price">
          {model.priceFrom.currency} {model.priceFrom.amount.toFixed(2)}
        </p>
        {showColors && (
          <>
            <p className="ox-m-section-title">Opciones disponibles</p>
            <div className="ox-m-swatch-row">
              {model.colors.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`ox-m-swatch${colorKey === c.key ? ' active' : ''}`}
                  style={swatchStyle(c.hex)}
                  onClick={() => setColor(c.key)}
                />
              ))}
            </div>
          </>
        )}
        {showSizes && (
          <div className="ox-m-details-sizes">
            <span>{sizeAxis} disponibles</span>
            <strong>{model.sizeRangeLabel}</strong>
            {hasSizeGuide && (
              <button type="button" className="ox-home-link" onClick={() => setSizeGuideOpen(true)}>
                Guía de tallas
              </button>
            )}
          </div>
        )}
        <p className="ox-m-section-title">Descripción</p>
        <p className="ox-hint">
          Producto personalizable. Elige las opciones disponibles al añadir a la cesta. El diseño se
          guarda y se genera el archivo de impresión al confirmar el pedido.
        </p>
      </BottomSheet>
      {sizeGuideOpen && hasSizeGuide && sizeGuideUrl && (
        <SizeGuideModal url={sizeGuideUrl} onClose={() => setSizeGuideOpen(false)} />
      )}
    </>
  );
}

function MobileCategoryBlocks({
  categories,
  categoryId,
  models,
  onSelectCategory,
  onPickModel,
  depth = 0,
}: {
  categories: Category[];
  categoryId: string;
  models: ModelSummary[];
  onSelectCategory: (id: string) => void;
  onPickModel: (id: string) => void;
  depth?: number;
}) {
  return (
    <>
      {categories.map((cat) => {
        const kids = cat.children ?? [];
        const active = categoryId === cat.id;
        return (
          <section key={cat.id} className="ox-m-cat-block" style={{ paddingLeft: depth * 12 }}>
            <button
              type="button"
              className={`ox-m-cat-head${active ? ' active' : ''}`}
              onClick={() => onSelectCategory(cat.id)}
            >
              {cat.label.toUpperCase()}
            </button>
            {active && (
              <div className="ox-m-product-row">
                {models.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className="ox-m-product-card"
                    onClick={() => onPickModel(m.id)}
                  >
                    <img src={m.thumbnail} alt="" />
                    <strong>{m.name}</strong>
                    <span>
                      {m.colorCount > 1 ? `${m.colorCount} opciones` : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {kids.length > 0 && (
              <MobileCategoryBlocks
                categories={kids}
                categoryId={categoryId}
                models={models}
                onSelectCategory={onSelectCategory}
                onPickModel={onPickModel}
                depth={depth + 1}
              />
            )}
          </section>
        );
      })}
    </>
  );
}

export function ChangeProductSheet({
  catalog,
  onClose,
}: {
  catalog: CatalogProvider;
  onClose: () => void;
}) {
  const changeModel = useEditorStore((s) => s.changeModel);
  const document = useEditorStore((s) => s.document);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryId, setCategoryId] = useState('populares');
  const [models, setModels] = useState<ModelSummary[]>([]);

  useEffect(() => {
    void catalog.listCategories().then((list) => {
      setCategories(Array.isArray(list) ? list : []);
    });
  }, [catalog]);

  useEffect(() => {
    void catalog.listModels({ categoryId }).then((list) => {
      setModels(Array.isArray(list) ? list : []);
    });
  }, [catalog, categoryId]);

  return (
    <BottomSheet title="Cambiar producto" onClose={onClose} tall>
      <MobileCategoryBlocks
        categories={categories}
        categoryId={categoryId}
        models={models}
        onSelectCategory={setCategoryId}
        onPickModel={(id) => {
          void catalog.getModel(id).then((full: ProductModel) => {
            changeModel(full, document.selection.colorKey);
            onClose();
          });
        }}
      />
    </BottomSheet>
  );
}

export function PreviewSheet({
  urls,
  loading = false,
  onClose,
}: {
  urls?: Partial<Record<ViewId, string>>;
  loading?: boolean;
  onClose: () => void;
}) {
  const model = useEditorStore((s) => s.model);
  const activeView = useEditorStore((s) => s.activeView);
  const setActiveView = useEditorStore((s) => s.setActiveView);
  const views = model?.views ?? [];
  const idx = Math.max(0, views.findIndex((v) => v.id === activeView));
  const mainUrl = urls?.[activeView];

  const go = (dir: -1 | 1) => {
    if (views.length < 2) return;
    const next = views[(idx + dir + views.length) % views.length];
    if (next) setActiveView(next.id);
  };

  // Spinner follows server/warmup only. Do not gate on <img onLoad> — cached
  // previews (after warmDesignPreviews) often never fire onLoad on mobile WebKit.
  const showEmpty = !loading && !mainUrl;

  return (
    <div className="ox-preview-sheet">
      <header>
        <span>Vista previa</span>
        <button type="button" onClick={onClose} aria-label="Cerrar">
          ✕
        </button>
      </header>
      <div className="ox-preview-canvas ox-preview-canvas-img">
        {mainUrl ? <img key={mainUrl} src={mainUrl} alt="" /> : null}
        {loading && (
          <div className="ox-preview-status ox-preview-status--overlay" aria-live="polite">
            <div className="ox-preview-spinner" aria-hidden />
            <p>Generando vista previa…</p>
          </div>
        )}
        {showEmpty && (
          <div className="ox-preview-status">
            <p>Sin vista previa</p>
          </div>
        )}
        {views.length > 1 && (
          <>
            <button
              type="button"
              className="ox-preview-nav ox-preview-nav--prev"
              onClick={() => go(-1)}
              aria-label="Vista anterior"
            >
              ‹
            </button>
            <button
              type="button"
              className="ox-preview-nav ox-preview-nav--next"
              onClick={() => go(1)}
              aria-label="Vista siguiente"
            >
              ›
            </button>
          </>
        )}
      </div>
      {views.length > 1 && (
        <div className="ox-preview-view-tabs">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              className={activeView === v.id ? 'active' : ''}
              onClick={() => setActiveView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
